import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  PipelineRunMethod,
  StartPipelineRunInput,
  StartPipelineRunResponse,
  startPipelineRun,
} from '../../api/taxPipeline';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useEscToClose } from '../../hooks/useEscToClose';
import '../CancelTradeGroupModal.css';
import './RunPipelineModal.css';

// =============================================================================
// RunPipelineModal — operator-facing dialog to start a tax pipeline run for a
// given tax year, set of cost-basis methods, and set of output formats.
//
// Modal chrome (overlay, header, footer, pill buttons) reuses the shared
// classes from CancelTradeGroupModal.css; view-level styling lives in
// RunPipelineModal.css.
//
// Submit calls POST /v1/tax/pipeline/runs via startPipelineRun. Server errors
// surface inline below the footer; the modal stays open and the submit button
// re-enables so the operator can retry or correct inputs. On success, the
// caller is handed the StartPipelineRunResponse before the modal closes.
// =============================================================================

const ALL_METHODS: PipelineRunMethod[] = ['HIFO', 'LIFO', 'FIFO'];
const DEFAULT_METHODS: PipelineRunMethod[] = ['HIFO'];
const ALL_FORMATS: string[] = [
  'FORM_8949_CSV',
  'TXF',
  'ACCOUNTANT_SUMMARY',
  'RECONCILIATION',
];

export interface RunPipelineModalProps {
  onClose: () => void;
  onSuccess: (run: StartPipelineRunResponse) => void;
}

const RunPipelineModal: React.FC<RunPipelineModalProps> = ({ onClose, onSuccess }) => {
  const defaultYear = useTaxYearStore((s) => s.selectedYear);
  const yearMax = new Date().getFullYear() + 1;

  const [taxYear, setTaxYear] = useState<number>(defaultYear);
  const [selectedMethods, setSelectedMethods] = useState<PipelineRunMethod[]>(
    DEFAULT_METHODS
  );
  const [selectedFormats, setSelectedFormats] = useState<string[]>(ALL_FORMATS);

  const mutation = useMutation({
    mutationFn: (input: StartPipelineRunInput) => startPipelineRun(input),
    onSuccess: (data) => {
      onSuccess(data);
      onClose();
    },
  });

  useEscToClose(onClose, { disabled: mutation.isPending });

  const toggleMethod = (method: PipelineRunMethod) => {
    setSelectedMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method]
    );
  };

  const toggleFormat = (format: string) => {
    setSelectedFormats((prev) =>
      prev.includes(format) ? prev.filter((f) => f !== format) : [...prev, format]
    );
  };

  const submitDisabled =
    mutation.isPending ||
    selectedMethods.length === 0 ||
    selectedFormats.length === 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (submitDisabled) return;
    mutation.mutate({
      taxYear,
      methods: selectedMethods,
      formats: selectedFormats,
    });
  };

  const errorMessage =
    mutation.error instanceof Error
      ? mutation.error.message
      : mutation.error
        ? 'Failed to start pipeline run'
        : null;

  return (
    <div
      className="modal-overlay"
      onClick={mutation.isPending ? undefined : onClose}
    >
      <div
        className="modal-content run-pipeline-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Run Pipeline</h2>
          <button
            type="button"
            className="close-button"
            onClick={onClose}
            disabled={mutation.isPending}
            aria-label="Close run pipeline form"
          >
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <fieldset className="run-pipeline-modal__field-group">
              <legend>Tax year</legend>
              <input
                type="number"
                min={2020}
                max={yearMax}
                className="form-input"
                value={taxYear}
                onChange={(e) => setTaxYear(Number(e.target.value))}
                disabled={mutation.isPending}
                required
                aria-label="Tax year"
              />
            </fieldset>

            <fieldset className="run-pipeline-modal__field-group">
              <legend>Methods</legend>
              <div className="run-pipeline-modal__checkbox-list">
                {ALL_METHODS.map((method) => (
                  <label key={method}>
                    <input
                      type="checkbox"
                      checked={selectedMethods.includes(method)}
                      onChange={() => toggleMethod(method)}
                      disabled={mutation.isPending}
                    />
                    {' '}
                    {method}
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="run-pipeline-modal__field-group">
              <legend>Formats</legend>
              <div className="run-pipeline-modal__checkbox-list">
                {ALL_FORMATS.map((format) => (
                  <label key={format}>
                    <input
                      type="checkbox"
                      checked={selectedFormats.includes(format)}
                      onChange={() => toggleFormat(format)}
                      disabled={mutation.isPending}
                    />
                    {' '}
                    {format}
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="modal-pill-button modal-pill-cancel"
              onClick={onClose}
              disabled={mutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-pill-button modal-pill-preview"
              disabled={submitDisabled}
            >
              {mutation.isPending ? 'Starting…' : 'Submit'}
            </button>
          </div>
          {errorMessage && (
            <div role="alert" className="run-pipeline-modal__error">
              {errorMessage}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default RunPipelineModal;
